import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { swalConfig, toastConfig } from '@/config';
import useRouteStore from '@/hooks/useRouteStore';
import { getContactLists, createContactList, deleteContactList } from '@/api';

const ContactLists = () => {
  const mySwal = withReactContent(Swal);
  const setRoute = useRouteStore((state) => state.setRoute);

  const [lists, setLists]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    setRoute([
      { name: 'Inicio',              url: '/administracion' },
      { name: 'Correos',             url: '/administracion/correos' },
      { name: 'Listas de contacto',  url: '/administracion/correos/listas' },
    ]);
    fetchLists();
  }, []);

  const fetchLists = () => {
    setLoading(true);
    getContactLists().then((res) => {
      setLists(res.data.result ?? res.data ?? []);
    }).finally(() => setLoading(false));
  };

  const handleCreate = async () => {
    const result = await mySwal.fire({
      ...swalConfig.input,
      title: 'Nueva lista de contacto',
      inputPlaceholder: 'Nombre de la lista...',
    });
    if (!result.isConfirmed || !result.value?.trim()) return;
    setCreating(true);
    try {
      await createContactList({ name: result.value.trim() });
      toast.success('Lista creada correctamente', toastConfig);
      fetchLists();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al crear la lista', toastConfig);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (list) => {
    const result = await mySwal.fire({
      ...swalConfig.delete,
      text: `¿Eliminar la lista "${list.name}"? Esta acción no se puede deshacer.`,
    });
    if (!result.isConfirmed) return;
    try {
      await deleteContactList(list._id);
      toast.success('Lista eliminada', toastConfig);
      setLists((prev) => prev.filter((l) => l._id !== list._id));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al eliminar la lista', toastConfig);
    }
  };

  return (
    <div className='font-montserrat w-full'>

      <div className='flex items-center justify-between pb-1 mb-5 border-b-2'>
        <h1 className='font-semibold text-xl'>Listas de contacto</h1>
        <button
          onClick={handleCreate}
          disabled={creating}
          className='flex items-center gap-1.5 bg-red-1 hover:bg-red-2 text-white text-sm font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
        >
          <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M12 4v16m8-8H4' />
          </svg>
          Crear lista
        </button>
      </div>

      {loading ? (
        <div className='flex flex-col gap-2'>
          {[...Array(4)].map((_, i) => (
            <div key={i} className='h-16 w-full rounded-lg animate-pulse bg-gray-100' />
          ))}
        </div>
      ) : lists.length === 0 ? (
        <p className='text-sm text-gray-400 italic text-center py-12'>No hay listas de contacto todavía.</p>
      ) : (
        <div className='flex flex-col gap-2'>
          {lists.map((list) => (
            <div
              key={list._id}
              className='flex items-center gap-4 bg-white border border-gray-100 rounded-lg px-4 py-3 shadow-sm'
            >
              {/* Name */}
              <div className='flex-1 min-w-0'>
                <p className='font-semibold text-gray-800 truncate'>{list.name}</p>
                <p className='text-xs text-gray-400 mt-0.5'>
                  Creada el {new Date(list.createdAt).toLocaleDateString()}
                </p>
              </div>

              {/* Contact count */}
              <div className='flex items-center gap-1.5 shrink-0 bg-gray-50 border border-gray-200 rounded-full px-3 py-1'>
                <svg className='w-3.5 h-3.5 text-gray-400' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                  <path strokeLinecap='round' strokeLinejoin='round' d='M17 20h5v-2a4 4 0 00-5-3.87M9 20H4v-2a4 4 0 015-3.87m6-4a4 4 0 11-8 0 4 4 0 018 0zm6 4a4 4 0 00-3-3.87' />
                </svg>
                <span className='text-xs font-semibold text-gray-600'>{list.total ?? 0} contactos</span>
              </div>

              {/* Actions */}
              <div className='flex items-center gap-2 shrink-0'>
                <Link
                  to={`/administracion/correos/listas/${list._id}`}
                  className='flex items-center gap-1 text-xs font-semibold text-red-1 border border-red-1 px-3 py-1.5 rounded-lg hover:bg-red-1 hover:text-white transition-colors'
                >
                  <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                    <path strokeLinecap='round' strokeLinejoin='round' d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' />
                    <path strokeLinecap='round' strokeLinejoin='round' d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                  </svg>
                  Gestionar
                </Link>
                <button
                  onClick={() => handleDelete(list)}
                  className='flex items-center gap-1 text-xs font-semibold text-gray-400 border border-gray-200 px-3 py-1.5 rounded-lg hover:border-red-1 hover:text-red-1 transition-colors'
                >
                  <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                    <path strokeLinecap='round' strokeLinejoin='round' d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                  </svg>
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactLists;
